/* Reactive store shared by all dashboard components (Vue.observable — no Vuex dependency). */
import Vue from 'vue'
import { translate } from './i18n/messages.js'

export function createStore(api, { lang = 'ar', dark = false, staticMode = false } = {}) {
  const state = Vue.observable({
    lang, dark, staticMode,
    data: null, strategic: null,
    loading: false, error: null,
    refreshing: false,
    version: 0,         // bumped after a refresh so the views re-mount and refetch
    details: {},        // id -> SurveyDetail (reactive map)
    detailMissing: {},  // id -> true when the API returned 404
    tip: null,          // { html, x, y } for the shared tooltip
  })
  const pending = {}
  const store = {
    state, api,
    t(key, vars) { return translate(state.lang, key, vars) },
    async load() {
      state.loading = true; state.error = null
      try {
        const [data, strategic] = await Promise.all([api.getSnapshot(), api.getStrategic().catch(() => null)])
        state.data = data; state.strategic = strategic
      } catch (e) { state.error = e }
      finally { state.loading = false }
    },
    /** Rebuild the server cache from the database, then drop everything held here and reload. */
    async refresh() {
      if (state.refreshing) return
      state.refreshing = true
      try {
        await api.refresh()
        const [data, strategic] = await Promise.all([api.getSnapshot(), api.getStrategic().catch(() => null)])
        state.details = {}; state.detailMissing = {}
        state.data = data; state.strategic = strategic; state.error = null
        state.version++
      } finally { state.refreshing = false }
    },
    /** Resolve one survey detail (cached). */
    survey(id) {
      id = +id
      if (state.details[id]) return Promise.resolve(state.details[id])
      if (state.detailMissing[id]) return Promise.reject(new Error('missing'))
      if (!pending[id]) pending[id] = api.getSurvey(id)
        .then((sv) => { Vue.set(state.details, id, sv); return sv })
        .catch((e) => { if (e && e.status === 404) Vue.set(state.detailMissing, id, true); throw e })
        .finally(() => { delete pending[id] })
      return pending[id]
    },
    /** Resolve many; missing ones are skipped (the caller sees what is in `details`). */
    surveys(ids) { return Promise.all(ids.map((id) => this.survey(id).catch(() => null))) },
    detailsMap() { const m = new Map(); for (const k in state.details) m.set(+k, state.details[k]); return m },
    setLang(l) { state.lang = l },
    setDark(d) { state.dark = d },
  }
  return store
}

/** Mixin: every component gets `$sd` (store), `D` (snapshot), `lang`, `dark`, and `t()`. */
export const sdMixin = {
  inject: { $sd: { from: 'sdStore' } },
  computed: {
    D() { return this.$sd.state.data || {} },
    ST() { return this.$sd.state.strategic },
    lang() { return this.$sd.state.lang },
    isAr() { return this.$sd.state.lang === 'ar' },
    dark() { return this.$sd.state.dark },
    staticMode() { return this.$sd.state.staticMode },
  },
  methods: {
    t(key, vars) { return this.$sd.t(key, vars) },
  },
}
